import { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const supplierSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().optional(),
  criticality: z.enum(['low', 'medium', 'high', 'critical']),
  notes: z.string().optional(),
  last_assessment: z.string().optional(),
  next_review: z.string().optional(),
  questionnaire_score: z.number().min(0).max(100).optional(),
  issues_count: z.number().min(0).optional(),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface SupplierEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: any;
  onSave: (id: string, data: any) => void;
  isSaving?: boolean;
}

export function SupplierEditDialog({ open, onOpenChange, supplier, onSave, isSaving }: SupplierEditDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      criticality: 'medium',
      issues_count: 0,
    },
  });

  useEffect(() => {
    if (supplier && open) {
      reset({
        name: supplier.name || '',
        category: supplier.category || '',
        criticality: supplier.criticality || 'medium',
        notes: supplier.notes || '',
        last_assessment: supplier.last_assessment || '',
        next_review: supplier.next_review || '',
        questionnaire_score: supplier.questionnaire_score || undefined,
        issues_count: supplier.issues_count || 0,
      });
    }
  }, [supplier, open, reset]);

  const lastAssessment = watch('last_assessment');
  const nextReview = watch('next_review');

  const onSubmit = (data: SupplierFormData) => {
    onSave(supplier.id, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Supplier</DialogTitle>
          <DialogDescription>
            Update supplier details and assessment information
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Supplier Name *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Enter supplier name"
            />
            {errors.name && (
              <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                {...register('category')}
                placeholder="e.g., IT Services, Cloud Provider"
              />
            </div>

            <div>
              <Label htmlFor="criticality">Criticality *</Label>
              <Select
                value={watch('criticality')}
                onValueChange={(value: any) => setValue('criticality', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Last Assessment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !lastAssessment && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {lastAssessment ? format(new Date(lastAssessment), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={lastAssessment ? new Date(lastAssessment) : undefined}
                    onSelect={(date) => setValue('last_assessment', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Next Review Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !nextReview && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {nextReview ? format(new Date(nextReview), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextReview ? new Date(nextReview) : undefined}
                    onSelect={(date) => setValue('next_review', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="questionnaire_score">Questionnaire Score (0-100)</Label>
              <Input
                id="questionnaire_score"
                type="number"
                min="0"
                max="100"
                {...register('questionnaire_score', { valueAsNumber: true })}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label htmlFor="issues_count">Issues Count</Label>
              <Input
                id="issues_count"
                type="number"
                min="0"
                {...register('issues_count', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Additional notes about this supplier"
              className="min-h-[100px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
